import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Email,
  Security,
} from '@mui/icons-material';
import axios from 'axios';

const PasswordChangeComponent = () => {
  const [step, setStep] = useState(0); // 0: Email, 1: Verify, 2: Change Password
  const [formData, setFormData] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [validationErrors, setValidationErrors] = useState({});

  const steps = ['Verify Email', 'Current Password', 'New Password'];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [field]: ''
    }));
    setMessage({ type: '', text: '' });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleEmailVerification = async () => {
    if (!validateEmail(formData.email)) {
      setValidationErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/verify-user-email`,
        { email: formData.email }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Email verified! Please enter your current password.' 
        });
        setStep(1);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setMessage({ 
          type: 'error', 
          text: 'Email not found. Please check your email address.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: 'Error verifying email. Please try again.' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCurrentPasswordVerification = async () => {
    if (!formData.currentPassword) {
      setValidationErrors({ currentPassword: 'Current password is required' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/verify-current-password`,
        { 
          email: formData.email,
          currentPassword: formData.currentPassword 
        }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Password verified! Now set your new password.' 
        });
        setStep(2);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setMessage({ 
          type: 'error', 
          text: 'Current password is incorrect. Please try again.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: 'Error verifying password. Please try again.' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    const errors = {};

    if (!validatePassword(formData.newPassword)) {
      errors.newPassword = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (formData.newPassword === formData.currentPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/change-password`,
        { 
          email: formData.email,
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword 
        }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Password changed successfully! You can now login with your new password.' 
        });
        
        // Reset form after successful change
        setTimeout(() => {
          setStep(0);
          setFormData({
            email: '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error changing password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    switch (step) {
      case 0:
        handleEmailVerification();
        break;
      case 1:
        handleCurrentPasswordVerification();
        break;
      case 2:
        handlePasswordChange();
        break;
      default:
        break;
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ borderRadius: 3 }}>
        <Box sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Security sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Change Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Update your account password securely
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={step} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Message Display */}
          {message.text && (
            <Alert 
              severity={message.type} 
              sx={{ mb: 3 }}
              onClose={() => setMessage({ type: '', text: '' })}
            >
              {message.text}
            </Alert>
          )}

          {/* Step 0: Email Verification */}
          {step === 0 && (
            <Box>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                onKeyPress={handleKeyPress}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
            </Box>
          )}

          {/* Step 1: Current Password */}
          {step === 1 && (
            <Box>
        
              <TextField
                fullWidth
                label="Current Password"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={handleInputChange('currentPassword')}
                onKeyPress={handleKeyPress}
                error={!!validationErrors.currentPassword}
                helperText={validationErrors.currentPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('current')}
                        edge="end"
                      >
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
            </Box>
          )}

          {/* Step 2: New Password */}
          {step === 2 && (
            <Box>
              <TextField
                fullWidth
                label="New Password"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleInputChange('newPassword')}
                error={!!validationErrors.newPassword}
                helperText={validationErrors.newPassword || 'At least 8 characters with uppercase, lowercase, and number'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('new')}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                onKeyPress={handleKeyPress}
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('confirm')}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            {step > 0 && (
              <Button
                variant="outlined"
                onClick={() => setStep(step - 1)}
                disabled={loading}
              >
                Back
              </Button>
            )}
            
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ ml: 'auto' }}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading 
                ? 'Processing...' 
                : step === 2 
                ? 'Change Password' 
                : step === 1 
                ? 'Verify Password' 
                : 'Verify Email'
              }
            </Button>
          </Box>

          {/* Help Text */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              {step === 0 && 'Enter the email address associated with your account'}
              {step === 1 && 'Use the temporary password provided in your welcome email'}
              {step === 2 && 'Create a strong password that you\'ll remember'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PasswordChangeComponent;